import { Dialog, Transition } from '@headlessui/react';
import clsx, { ClassValue } from 'clsx';
import { ModalSaving, useModalSaving } from 'hooks/useModalSaving';
import { FC, Fragment, ReactNode, useRef, useState } from 'react';
import { TModalRound, TModalSize } from 'utils/types';

// components
import { useConfirmDialogStore } from 'components/modals/ModalConfirm';
import OrderFlowTimeline from 'components/OrderFlowTimeline';

interface IModal {
  open: boolean;
  onClose: (x: boolean) => void;
  title?: string | ReactNode;
  subtitle?: string | ReactNode;
  classNameTitle?: ClassValue;
  children?: ReactNode;
  size?: TModalSize;
  round?: TModalRound;
  bgType?: 'normal' | 'grey' | 'transparent';
  isConfirmOpen?: boolean;
  closeOnOutsideClick?: boolean;
}

const bgTypes = {
  normal: 'bg-white shadow-xl ',
  grey: 'bg-[#F3F3F3] shadow-xl',
  transparent: 'bg-transparent'
};

const sizes = {
  xs: 'max-w-xs',
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
  full: 'w-full'
};

const rounded = {
  sm: 'rounded-sm',
  rounded: 'rounded',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  '3xl': 'rounded-3xl',
  full: 'rounded-full'
};

const Modal: FC<IModal> = ({
  open,
  onClose,
  title,
  subtitle,
  classNameTitle,
  size = 'md',
  round = 'rounded',
  bgType = 'normal',
  children,
  isConfirmOpen = false,
  closeOnOutsideClick = true
}) => {
  const close = () => onClose(!open);
  const buttonRef = useRef(null);

  const [isSaving, setIsSaving] = useState<boolean>(false);

  return (
    <ModalSaving.Provider value={{ isSaving, setIsSaving }}>
      <Transition appear show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          onClose={() => {
            if (isSaving) return;
            if (!closeOnOutsideClick) return;
            close();
          }} // onClose={() => {}}
          initialFocus={buttonRef}
        >
          <div className="flex items-center justify-center min-h-screen">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              {/* Use the overlay to style a dim backdrop for your dialog */}
              <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div
                className={clsx(
                  sizes[size],
                  rounded[round],
                  bgTypes[bgType],
                  title && 'overflow-hidden',
                  'inline-block w-full text-left align-middle transition-all transform',
                  'relative'
                )}
              >
                {!title && (
                  <div className="absolute top-0 right-0 -mt-2 -mr-2">
                    {/* <Button
                      type="button"
                      variant="invisible"
                      size="inline"
                      rounded="full"
                      ref={buttonRef}
                      disabled={isSaving}
                      classNameButton="p-1"
                      onClick={() => close()}
                    >
                      <XIcon className="w-3 h-3" />
                    </Button> */}
                  </div>
                )}
                <div className="divide-y">
                  {title && (
                    <Dialog.Title
                      as="div"
                      className={clsx(
                        'text-base  font-Sohne-Semibold leading-6 p-4 capitalize flex justify-between items-center',
                        classNameTitle
                      )}
                    >
                      <div>
                        {title} <span className="font-Sohne-Light">{subtitle}</span>
                      </div>

                      {/* <Button
                        type="button"
                        variant="invisible"
                        size="inline"
                        rounded="full"
                        ref={buttonRef}
                        disabled={isSaving}
                        classNameButton="p-1"
                        onClick={() => close()}
                      >
                        <XIcon className="w-5 h-5" />
                      </Button> */}
                    </Dialog.Title>
                  )}

                  <div className="p-4">{children}</div>
                </div>
              </div>
            </Transition.Child>
          </div>
          {!isConfirmOpen && <ConfirmDialog />}
          <div id="picker-portal" />
        </Dialog>
      </Transition>
    </ModalSaving.Provider>
  );
};

export default Modal;

const chipStyles: any = {
  warning: { bg: 'bg-[#FEFECB]', text: 'text-[#7A6B00]', label: 'Confirmation' },
  error:   { bg: 'bg-red-100',   text: 'text-red-700',   label: 'Warning'       },
  success: { bg: 'bg-[#D6EEE8]', text: 'text-[#0F5342]', label: 'Confirmation'  }
};

export const ConfirmDialog: FC = () => {
  const { flowStep, flowDirection, message, onSubmit, close, open, variant = 'error' } = useConfirmDialogStore();
  const { setIsSaving } = useModalSaving();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    setIsSaving(false);
    try {
      await onSubmit(true);
      setIsLoading(false);
      setIsSaving(false);
      close();
    } catch (err) {
      setIsLoading(false);
      setIsSaving(false);
    }
  };

  const chip = chipStyles[variant] ?? chipStyles.error;

  return (
    <Modal
      open={open}
      onClose={close}
      classNameTitle="bg-white text-[#29251f] font-Sohne-Regular"
      isConfirmOpen
      size="sm"
    >
      <div className="space-y-4 p-1">
        {flowStep && <OrderFlowTimeline targetStep={flowStep} direction={flowDirection} />}

        <div className="space-y-3">
          <span className={clsx('font-mono uppercase text-xxs px-2 py-0.5 rounded tracking-wide inline-block', chip.bg, chip.text)}>
            {chip.label}
          </span>
          <p className="text-xs text-[#6B6560] leading-relaxed">{message}</p>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            disabled={isLoading}
            onClick={close}
            className="text-xs px-5 py-1.5 rounded-full bg-[#F0EDE8] text-[#6B6560] hover:bg-[#e8e4de] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isLoading}
            onClick={handleConfirm}
            className={clsx(
              'text-xs px-5 py-1.5 rounded-full bg-[#FFFDA5] text-black hover:bg-[#FFF799] transition-colors',
              isLoading && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isLoading ? 'Confirming…' : 'Confirm'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
